import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { MapPin, Menu, Phone, Search, ShoppingBasket, Truck, UserRound, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { shopWhatsAppUrl, staticSite } from "../config";

const searchCategories = [
  { value: "", label: "All groceries", image: "pantry" },
  { value: "staples", label: "Rice & grains", image: "pantry" },
  { value: "flours", label: "Flours", image: "pantry" },
  { value: "soup", label: "Soup essentials", image: "pantry" },
  { value: "fresh", label: "Fresh produce", image: "fresh" },
  { value: "meat-fish", label: "Meat & fish", image: "fresh" },
  { value: "drinks", label: "Drinks & snacks", image: "snacks" },
];

const popularSearches = ["Garri", "Egusi", "Pounded yam flour", "Palm oil", "Crayfish", "Plantain"];

function Brand() {
  return <Link to="/" className="brand" aria-label="Jummy's Global home">
    <span className="brand-mark">JG</span>
    <span className="brand-copy"><strong>Jummy's</strong><small>Global Groceries</small></span>
  </Link>;
}

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { items } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const count = items.reduce((sum, item) => sum + item.quantity, 0);
  const accountPath = user?.role === "admin" ? "/admin" : user?.role === "rider" ? "/rider" : "/account";

  useEffect(() => { setMobileOpen(false); setSearchOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!searchOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => inputRef.current?.focus(), 100);
    const closeOnEscape = (event) => event.key === "Escape" && setSearchOpen(false);
    window.addEventListener("keydown", closeOnEscape);
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener("keydown", closeOnEscape); };
  }, [searchOpen]);

  const runSearch = (query = search, category = "") => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("search", query.trim());
    if (category) params.set("category", category);
    navigate(`/shop${params.toString() ? `?${params}` : ""}`);
    setSearchOpen(false);
  };

  return <>
    <header className="site-header">
      <div className="topbar">
        <div className="topbar-shell">
          <div className="topbar-contact">
            <a href="tel:+447901301388"><Phone />07901 301388</a>
            <a href="https://maps.google.com/?q=49+Luton+High+Street+Chatham+ME5+7LP" target="_blank" rel="noreferrer"><MapPin />49 Luton High Street, Chatham, ME5 7LP</a>
          </div>
          <div className="topbar-actions">
            <span className="delivery-pill"><Truck /> Local delivery & easy collection</span>
            {staticSite ? <a href={shopWhatsAppUrl} className="topbar-account" target="_blank" rel="noreferrer" aria-label="Message Jummy's Global on WhatsApp"><Phone /> Order help</a> : <Link to={accountPath} className="topbar-account" aria-label={user ? "Open account" : "Sign in"}><UserRound />{user ? `Hi, ${user.name.split(" ")[0]}` : "Sign in"}</Link>}
          </div>
        </div>
      </div>
      <div className="nav-shell">
        <Brand />
        <nav className={mobileOpen ? "main-nav is-open" : "main-nav"} aria-label="Main navigation">
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/shop">Shop</NavLink>
          <a href="/#departments">Departments</a>
          <a href="/#story">About us</a>
          <a href="/#contact">Contact</a>
          <a href="/#visit">Visit us</a>
          {staticSite ? <a href={shopWhatsAppUrl} target="_blank" rel="noreferrer">Order help</a> : <NavLink to={accountPath}>{user ? "My account" : "Sign in"}</NavLink>}
        </nav>
        <div className="nav-actions">
          <button className="search-trigger" onClick={() => setSearchOpen(true)} aria-label="Search groceries"><Search /></button>
          <Link to="/cart" className="nav-cart"><ShoppingBasket /> <span className="cart-label">My basket</span>{count > 0 && <b>{count}</b>}</Link>
          <button className="nav-toggle" onClick={() => setMobileOpen((value) => !value)} aria-label="Toggle navigation" aria-expanded={mobileOpen}>{mobileOpen ? <X /> : <Menu />}</button>
        </div>
      </div>
      <nav className="department-nav" aria-label="Popular grocery departments">
        <div className="page-shell"><Link to="/shop"><b>Shop all</b></Link><Link to="/shop?category=staples">Rice, beans & grains</Link><Link to="/shop?category=flours">Flours & swallow</Link><Link to="/shop?category=soup">Soup essentials</Link><Link to="/shop?category=fresh">Fresh produce</Link><Link to="/shop?category=meat-fish">Meat & fish</Link><Link to="/shop?category=drinks">Drinks & snacks</Link></div>
      </nav>
    </header>

    {searchOpen && <div className="search-overlay" role="dialog" aria-modal="true" aria-labelledby="search-title">
      <button className="search-close" onClick={() => setSearchOpen(false)} aria-label="Close search"><X /></button>
      <div className="search-overlay-content">
        <p className="search-kicker">Jummy's Global Groceries</p>
        <h2 id="search-title">What are you shopping for?</h2>
        <form className="overlay-search-form" onSubmit={(event) => { event.preventDefault(); runSearch(); }}>
          <input ref={inputRef} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search rice, garri, egusi, drinks…" aria-label="Search groceries" />
          <button aria-label="Submit search"><Search /></button>
        </form>
        <div className="overlay-categories" aria-label="Grocery departments">
          {searchCategories.map((category) => <button key={category.label} onClick={() => runSearch("", category.value)} className={!category.value ? "active" : ""}>
            <span className={`search-category-${category.image}`} />{category.label}
          </button>)}
        </div>
        <div className="popular-searches">
          <p><Search /> Popular searches</p>
          <div>{popularSearches.map((term) => <button key={term} onClick={() => runSearch(term)}>{term}</button>)}</div>
        </div>
      </div>
    </div>}
  </>;
}

export default Navbar;
